/**
 * Marketing attribution capture.
 *
 * Campaign tags arrive on the landing URL — an ad click, a QR code, an email link —
 * but a visitor usually fills in a form several pages later, by which point the query
 * string is long gone. We read the tags once on arrival and hold them for the rest of
 * the browsing session so they are still available at submit time.
 *
 * First touch wins: once a value is recorded it is never overwritten, so someone who
 * arrives from a campaign and then browses the site is still credited to that campaign.
 *
 * Every path is wrapped defensively. Attribution is evidence, not a feature — if
 * storage is unavailable (private browsing, blocked cookies) it degrades to blanks
 * rather than throwing and taking a form submission down with it.
 */

const STORAGE_KEY = 'dv_attribution';
const MAX_LEN = 200;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export const EMPTY_ATTRIBUTION = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  utm_term: '',
  promo_code: '',
};

function safeRead() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function safeWrite(value) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable — carry on with what we have in memory */
  }
}

function clean(value) {
  return (value || '').trim().slice(0, MAX_LEN);
}

/**
 * Read campaign tags off the current URL and merge them into the stored set.
 * Safe to call more than once — existing values are preserved.
 */
export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = safeRead();
    const next = { ...stored };
    let changed = false;

    UTM_KEYS.forEach((key) => {
      const value = clean(params.get(key));
      if (value && !next[key]) {
        next[key] = value;
        changed = true;
      }
    });

    // A promo code may arrive as either ?promo= or ?code=
    const promo = clean(params.get('promo') || params.get('code'));
    if (promo && !next.promo_code) {
      next.promo_code = promo;
      changed = true;
    }

    if (changed) safeWrite(next);
    return { ...EMPTY_ATTRIBUTION, ...next };
  } catch {
    return { ...EMPTY_ATTRIBUTION };
  }
}

/** Everything captured this session, with blanks for anything never seen. */
export function getAttribution() {
  return { ...EMPTY_ATTRIBUTION, ...safeRead() };
}
